import { useNavigate } from 'react-router-dom';
import logoSolaGold from '@/assets/sola-notes-gold-sm.png';
import logoSolaLight from '@/assets/sola-notes-light-sm.png';
import { useAppContext } from '@/contexts/AppContext';

type Variant = 'gold' | 'light' | 'dark';

// Inject preload hints as early as possible so the LCP logo is discoverable
// from the initial document parse (improves LCP request discovery).
if (typeof document !== 'undefined') {
  const ensurePreload = (href: string) => {
    if (document.head.querySelector(`link[rel="preload"][href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    (link as HTMLLinkElement & { fetchPriority?: string }).fetchPriority = 'high';
    document.head.appendChild(link);
  };
  ensurePreload(logoSolaGold);
  ensurePreload(logoSolaLight);
}

const SolaLogo = ({
  size = 'md',
  className = '',
  clickable = true,
  variant = 'gold',
  showWordmark = true,
}: {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  clickable?: boolean;
  variant?: Variant;
  showWordmark?: boolean;
}) => {
  const navigate = useNavigate();
  const { user } = useAppContext();
  // Icon height + matched wordmark size form a single optical lockup.
  // The wordmark uses the brand serif (Lora) typeset live — never a PNG —
  // so it stays perfectly crisp at any density and pairs exactly with the icon.
  const heightClass =
    size === 'sm' ? 'h-9' : size === 'md' ? 'h-12' : 'h-16';
  const pixelSize = size === 'sm' ? 36 : size === 'md' ? 48 : 64;

  // Wordmark optical size — calibrated so the cap-height roughly matches
  // the icon's inner content height, creating the impression of a single mark.
  const wordmarkSizeClass =
    size === 'sm' ? 'text-[18px]' : size === 'md' ? 'text-[24px]' : 'text-[32px]';

  // Tighter gap on small/medium so icon and text read as one unit.
  const gapClass = size === 'sm' ? 'gap-2' : size === 'md' ? 'gap-2.5' : 'gap-3.5';

  const src = variant === 'light' ? logoSolaLight : logoSolaGold;
  const wordmarkColorClass =
    variant === 'light' ? 'text-[#F5EDD8]' : 'text-[#2D1F12]';

  const content = (
    <span className={`inline-flex items-center ${gapClass}`}>
      <img
        src={src}
        alt="Sola Notes"
        width={pixelSize}
        height={pixelSize}
        className={`${heightClass} w-auto object-contain shrink-0`}
        loading="eager"
        decoding="async"
        // @ts-expect-error fetchpriority is a valid HTML attribute, types lag behind
        fetchpriority="high"
      />
      {showWordmark && (
        <span
          aria-hidden="true"
          className={`font-serif font-medium tracking-[-0.01em] leading-none ${wordmarkSizeClass} ${wordmarkColorClass}`}
        >
          Sola Notes
        </span>
      )}
    </span>
  );

  if (clickable) {
    return (
      <button
        onClick={() => {
          const isMemberArea = window.location.pathname === '/app' || 
                             window.location.pathname.startsWith('/generate') || 
                             window.location.pathname.startsWith('/profile') ||
                             window.location.pathname.startsWith('/assinatura') ||
                             window.location.pathname.startsWith('/biblioteca') ||
                             window.location.pathname.startsWith('/note/');
          
          if (isMemberArea) {
            navigate(user ? '/app' : '/');
          } else {
            navigate('/');
          }
        }}
        aria-label="Sola Notes"
        className={`inline-flex items-center transition-opacity hover:opacity-80 ${className}`}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={`inline-flex items-center ${className}`}>
      {content}
    </div>
  );
};

export default SolaLogo;
